import { Component, OnInit, signal, inject } from '@angular/core';
import { NonNullableFormBuilder, FormGroup, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { LoginForm } from '../interfaces/forms';


@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  imports: [FormsModule, ReactiveFormsModule, RouterLink]
})
export class LoginComponent implements OnInit {
  private fb = inject(NonNullableFormBuilder);
  private router = inject(Router);
  private auth = inject(AuthService);

  loginForm!: FormGroup<{
    email: FormControl<string>;
    password: FormControl<string>;
  }>;

  // UI state signals
  submitted = signal(false);
  loading = signal(false);
  loginMSG = signal('Login');
  successMessage = signal<string | null>(null);
  errorMessage = signal<string | null>(null);
  passwordType = signal('password');
  show = signal(false);

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      email: this.fb.control('', [Validators.required, Validators.email]),
      password: this.fb.control('', [Validators.required])
    });
  }

  onSubmit(): void {
    // Prevent double submission
    if (this.loading()) return;

    this.submitted.set(true);
    this.successMessage.set(null);
    this.errorMessage.set(null);

    // Stop here if form is invalid
    if (this.loginForm.invalid) {
      this.errorMessage.set('Please enter valid email and password.');
      return;
    }

    this.loginMSG.set('');
    this.loading.set(true);

    // Prepare data to send to backend - getRawValue() returns typed LoginForm
    const credentials: LoginForm = this.loginForm.getRawValue();


    // Send login data to PHP backend
    this.auth.login(credentials).subscribe({
      next: (response: any) => {
        this.loading.set(false);
        this.loginMSG.set('Login');
        if (response.success === true) {
          this.loginForm.reset();
          this.submitted.set(false);
          if (credentials.email === 'assistant@adsplat.me') {
            this.router.navigate(['/signup']);
          } else {
            this.router.navigate(['/member']);
          }
        } else {
          this.errorMessage.set(response.message || 'Login failed. Please check your credentials.');
        }
      },
      error: (error: any) => {
        // Some RxJS throwError factories may pass a function; normalize to the actual error object
        const httpError: HttpErrorResponse = (typeof error === 'function') ? error() : error;
        this.loading.set(false);
        this.loginMSG.set('Login');

        // Extract backend-provided message if present
        const backendMessage = httpError && httpError.error && httpError.error.message;

        // Handle specific HTTP error codes from the backend (use defaults matching tests)
        if (httpError && httpError.status === 401) {
          this.errorMessage.set(backendMessage || 'Invalid email or password');
        } else if (httpError && httpError.status === 403) {
          this.errorMessage.set(backendMessage || 'Account not verified');
        } else if (httpError && httpError.status === 400) {
          this.errorMessage.set(backendMessage || 'Missing required fields');
        } else {
          this.errorMessage.set('An unexpected error occurred. Please try again later.');
        }
      }
    });

  }

  get rf() {
    return this.loginForm.controls;
  }

  onShowHide() {
    if (this.passwordType() === 'password') {
      this.passwordType.set('text');
      this.show.set(true);
    } else {
      this.passwordType.set('password');
      this.show.set(false);
    }
  }

}
